import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Select from "react-select";

import Section from "../../../components/common/form/Section";
import Grid from "../../../components/common/form/Grid";
import Input from "../../../components/common/form/Input";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

const CreateEngagement = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);

  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);

  const [form, setForm] = useState({
    assignmentName: "",
    financialYear: "",
    clientId: "",
    categoryId: "",
    assignmentTypeID: "",
    startDate: "",
    endDate: "",
    scope: "",
    status: "Active"
  });

  const [lineItems, setLineItems] = useState([]);

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // ================= INITIAL LOAD =================
  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (isEdit && id && clients.length > 0) {
      loadAssignment();
    }
  }, [id, clients]);

  const loadInitialData = async () => {
    try {
      const token = localStorage.getItem("token");

      const [clientsRes, metaRes] = await Promise.all([
        axios.get(`${API_URL}/api/clients`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/assignments/meta`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      // Clients
      const clientOptions = clientsRes.data.map(c => ({
        value: c.ClientID,
        label: c.ClientName
      }));

      setClients(clientOptions);

      // Meta
      setCategories(metaRes.data.categories);
      setTypes(metaRes.data.types);

    } catch (err) {
      console.error("Error loading initial data:", err);
    }
  };

  // ================= LOAD FOR EDIT =================
  const loadAssignment = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(
        `${API_URL}/api/assignments/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { assignment, lineItems } = res.data;

      setForm({
        assignmentName: assignment.AssignmentName || "",
        financialYear: assignment.FinancialYear || "",
        clientId: assignment.ClientID || "",
        categoryId: assignment.CategoryID || "",
        assignmentTypeID: assignment.AssignmentTypeID || "",
        startDate: assignment.StartDate?.split("T")[0] || "",
        endDate: assignment.EndDate?.split("T")[0] || "",
        scope: assignment.Scope || "",
        status: assignment.Status || "Active"
      });

      const selected = clients.find(c => c.value === assignment.ClientID);
      if (selected) setSelectedClient(selected);

      setLineItems(
        lineItems.map(item => ({
          serviceName: item.ServiceName,
          recurrenceType: item.RecurrenceType,
          startDate: item.StartDate?.split("T")[0] || "",
          endDate: item.EndDate?.split("T")[0] || "",
          feeAmount: item.FeeAmount || "",
          billingFrequency: item.BillingFrequency || ""
        }))
      );

    } catch (err) {
      console.error("Error loading assignment:", err);
    }
  };

  // ================= LINE ITEMS =================
  const addLineItem = () => {
    setLineItems(prev => [
      ...prev,
      {
        serviceName: "",
        recurrenceType: "",
        startDate: "",
        endDate: "",
        feeAmount: "",
        billingFrequency: ""
      }
    ]);
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    setLineItems(updated);
  };

  const removeLineItem = (index) => {
    const updated = [...lineItems];
    updated.splice(index, 1);
    setLineItems(updated);
  };

  // ================= SUBMIT =================
  const handleSubmit = async () => {
    if (!form.clientId) {
      alert("Please select a client");
      return;
    }

    if (!form.assignmentTypeID) {
      alert("Please select assignment type");
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const payload = {
        ...form,
        lineItems
      };

      if (isEdit) {
        await axios.put(
          `${API_URL}/api/assignments/${id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        navigate(`/assignments/${id}`);
      } else {
        const res = await axios.post(
          `${API_URL}/api/assignments`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        navigate(`/assignments/${res.data.AssignmentID}`);
      }

    } catch (err) {
      console.error("Save error:", err.response?.data || err);
    } finally {
      setLoading(false);
    }
  };

  // ================= UI =================
  return (
    <div className="max-w-6xl mx-auto">

      <h2 className="text-xl font-bold mb-6">
        {isEdit ? "Edit Engagement" : "Create Engagement"}
      </h2>

      <div className="space-y-6">

        {/* ================= DETAILS ================= */}
        <Section title="Engagement Details">
          <Grid>

            <div className="col-span-2">
              <label className="block text-sm font-medium mb-2">
                Client
              </label>
              <Select
                options={clients}
                value={selectedClient}
                onChange={(selected) => {
                  setSelectedClient(selected);
                  update("clientId", selected?.value || "");
                }}
                placeholder="Search client..."
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Assignment Category
              </label>
              <select
                className="border rounded-xl px-3 py-2 w-full"
                value={form.categoryId}
                onChange={e => {
                  update("categoryId", e.target.value);
                  update("assignmentTypeID", "");
                }}
              >
                <option value="">Select Category</option>
                {categories.map(cat => (
                  <option key={cat.CategoryID} value={cat.CategoryID}>
                    {cat.CategoryName}
                  </option>
                ))}
              </select>
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Assignment Type
              </label>
              <select
                className="border rounded-xl px-3 py-2 w-full"
                value={form.assignmentTypeID}
                onChange={e => update("assignmentTypeID", e.target.value)}
              >
                <option value="">Select Type</option>
                {types
                  .filter(t => t.CategoryID === form.categoryId)
                  .map(type => (
                    <option key={type.AssignmentTypeID} value={type.AssignmentTypeID}>
                      {type.TypeName}
                    </option>
                  ))}
              </select>
            </div>

            <Input
              label="Engagement Name"
              value={form.assignmentName}
              onChange={e => update("assignmentName", e.target.value)}
            />

            <Input
              label="Financial Year"
              value={form.financialYear}
              onChange={e => update("financialYear", e.target.value)}
            />

            <Input
              type="date"
              label="Start Date"
              value={form.startDate}
              onChange={e => update("startDate", e.target.value)}
            />

            <Input
              type="date"
              label="End Date"
              value={form.endDate}
              onChange={e => update("endDate", e.target.value)}
            />

          </Grid>
        </Section>

        {/* ================= SCOPE ================= */}
        <Section title="Scope">
          <textarea
            rows={4}
            className="border rounded-xl px-3 py-2 w-full"
            value={form.scope}
            onChange={e => update("scope", e.target.value)}
          />
        </Section>

        {/* ================= LINE ITEMS ================= */}
        <Section title="Line Items / Services">

          <div className="space-y-6">

            {lineItems.map((item, index) => (
              <div key={index} className="border rounded-xl p-4 bg-slate-50 space-y-4">

                <Grid>
                  <Input
                    label="Service Name"
                    value={item.serviceName}
                    onChange={e => updateLineItem(index, "serviceName", e.target.value)}
                  />

                  <Input
                    label="Recurrence"
                    value={item.recurrenceType}
                    onChange={e => updateLineItem(index, "recurrenceType", e.target.value)}
                  />

                  <Input
                    type="date"
                    label="Start Date"
                    value={item.startDate}
                    onChange={e => updateLineItem(index, "startDate", e.target.value)}
                  />

                  <Input
                    type="date"
                    label="End Date"
                    value={item.endDate}
                    onChange={e => updateLineItem(index, "endDate", e.target.value)}
                  />

                  <Input
                    label="Fee Amount"
                    value={item.feeAmount}
                    onChange={e => updateLineItem(index, "feeAmount", e.target.value)}
                  />

                  <Input
                    label="Billing Frequency"
                    value={item.billingFrequency}
                    onChange={e => updateLineItem(index, "billingFrequency", e.target.value)}
                  />
                </Grid>

                <div className="flex justify-end">
                  <button
                    onClick={() => removeLineItem(index)}
                    className="text-red-600 text-sm font-semibold"
                  >
                    Remove
                  </button>
                </div>

              </div>
            ))}

            <button
              onClick={addLineItem}
              className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm"
            >
              + Add Line Item
            </button>

          </div>

        </Section>

        {/* ================= ACTIONS ================= */}
        <div className="flex justify-end gap-3">
          <button
            className="px-4 py-2 border rounded-xl"
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl"
          >
            {loading ? "Saving..." : isEdit ? "Update Engagement" : "Create Engagement"}
          </button>
        </div>

      </div>
    </div>
  );
};

export default CreateEngagement;
